import React, { useMemo, useState } from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import {
  flexRender,
  getCoreRowModel,
  type ColumnDef,
  useReactTable,
} from "@tanstack/react-table";
import { BiCopy } from "react-icons/bi";
import { TailSpin } from "react-loader-spinner";
import { toast } from "react-hot-toast";

import { getAssetImage } from "../../constant";
import type { BaseRowItem } from "../../components/data-table";
import { formatOrdinalActivityDate } from "./userActivityDateFormat";
import {
  networkDisplayLabel,
  sortWallets,
  truncateAddress,
  type UserWalletRecord,
  type WalletSegment,
} from "./userActivityWalletUtils";

export type UserActivityAddressRow = BaseRowItem & {
  raw: UserWalletRecord;
  assetSymbol: string;
  assetImage?: string;
  networkLabel: string;
  address: string;
  addressShort: string;
  trailingLabel: string;
};

function buildRows(wallets: UserWalletRecord[]): UserActivityAddressRow[] {
  return sortWallets(wallets).map((wallet, index) => {
    const symbol = (wallet.currency ?? "").toUpperCase();
    const trailingLabel = formatOrdinalActivityDate(wallet.createdAt);

    return {
      id: wallet._id ?? `${symbol}-${wallet.network}-${index}`,
      raw: wallet,
      assetSymbol: symbol,
      assetImage: getAssetImage(symbol),
      networkLabel: networkDisplayLabel(wallet.network),
      address: wallet.address ?? "—",
      addressShort: wallet.address ? truncateAddress(wallet.address) : "—",
      trailingLabel,
    };
  });
}

function addressColClass(id: string): string {
  switch (id) {
    case "asset":
      return "w-[18%] min-w-[100px]";
    case "network":
      return "w-[16%] min-w-[90px]";
    case "address":
      return "w-[40%] min-w-[180px]";
    case "trailing":
      return "w-[26%] min-w-[140px] text-right";
    default:
      return "min-w-0";
  }
}

function CopyAddressButton({ address }: { address: string }) {
  const [copied, setCopied] = useState(false);

  if (!address || address === "—") return null;

  return (
    <CopyToClipboard
      text={address}
      onCopy={() => {
        setCopied(true);
        toast.success("Address copied");
        setTimeout(() => setCopied(false), 2000);
      }}
    >
      <button
        type="button"
        className="ml-2 inline-flex shrink-0 items-center justify-center rounded-md p-1 text-[#869AA9] transition-colors hover:bg-[#273037] hover:text-[#BEE1FB]"
        aria-label="Copy wallet address"
      >
        {copied ? (
          <span className="text-[10px] font-semibold uppercase tracking-wide text-[#359943]">
            Copied
          </span>
        ) : (
          <BiCopy className="text-[18px]" />
        )}
      </button>
    </CopyToClipboard>
  );
}

function createColumns(): ColumnDef<UserActivityAddressRow, unknown>[] {
  return [
    {
      id: "asset",
      accessorKey: "assetSymbol",
      header: () => (
        <span className="text-[12px] font-medium text-[#869AA9]">Asset</span>
      ),
      cell: ({ row }) => (
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#0D1115]">
            {row.original.assetImage ? (
              <img
                src={row.original.assetImage}
                alt={row.original.assetSymbol}
                className="h-full w-full object-contain"
              />
            ) : (
              <span className="text-[10px] font-bold text-[#869AA9]">
                {row.original.assetSymbol.slice(0, 3)}
              </span>
            )}
          </div>
          <span className="truncate text-[13px] font-semibold text-white">
            {row.original.assetSymbol}
          </span>
        </div>
      ),
    },
    {
      id: "network",
      accessorKey: "networkLabel",
      header: () => (
        <span className="text-[12px] font-medium text-[#869AA9]">Network</span>
      ),
      cell: ({ getValue }) => (
        <span className="text-[13px] text-[#C6D0D7]">{String(getValue())}</span>
      ),
    },
    {
      id: "address",
      accessorKey: "addressShort",
      header: () => (
        <span className="text-[12px] font-medium text-[#869AA9]">
          Wallet Address
        </span>
      ),
      cell: ({ row }) => (
        <div className="flex min-w-0 items-center">
          <span
            className="truncate font-mono text-[12px] text-[#BEE1FB] sm:text-[13px]"
            title={row.original.address}
          >
            {row.original.addressShort}
          </span>
          <CopyAddressButton address={row.original.address} />
        </div>
      ),
    },
    {
      id: "trailing",
      accessorKey: "trailingLabel",
      header: () => (
        <span className="block w-full text-right text-[12px] font-medium text-[#869AA9]">
          Added
        </span>
      ),
      cell: ({ getValue }) => (
        <span className="block w-full text-right text-[13px] font-medium text-white">
          {String(getValue())}
        </span>
      ),
    },
  ];
}

export type UserActivityAddressesTableProps = {
  wallets: UserWalletRecord[];
  segment: WalletSegment;
  isLoading?: boolean;
  emptyImage: string;
};

export function UserActivityAddressesTable({
  wallets,
  segment,
  isLoading = false,
  emptyImage,
}: UserActivityAddressesTableProps) {
  const data = useMemo(() => buildRows(wallets), [wallets]);

  const columns = useMemo(() => createColumns(), []);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => String(row.id),
  });

  const segmentLabel =
    segment === "otc" ? "OTC" : segment === "sell" ? "Sell" : "Deposit";

  if (isLoading && wallets.length === 0) {
    return (
      <div className="mt-8 flex h-[320px] items-center justify-center">
        <TailSpin color="#0B75C2" height={50} width={70} />
      </div>
    );
  }

  if (!isLoading && wallets.length === 0) {
    return (
      <div className="mt-10 flex h-[420px] flex-col items-center justify-center rounded-xl border border-[#1C2227] bg-[#192025]">
        <img src={emptyImage} alt="" className="opacity-80" />
        <h4 className="pt-3 text-[14px] font-bold text-[#869AA9]">
          No {segmentLabel.toLowerCase()} addresses
        </h4>
        <p className="pt-1 text-[12px] text-[#617889]">
          {segmentLabel} wallet addresses will appear here
        </p>
      </div>
    );
  }

  return (
    <div className="mt-4 w-full">
      <h4 className="mb-4 text-[15px] font-semibold text-white">
        {segmentLabel} addresses ({wallets.length})
      </h4>
      <div className="overflow-hidden rounded-xl border border-[#1C2227] bg-[#192025]">
        <div className="max-h-[560px] overflow-auto scrollbar-thin scrollbar-track-[#192025] scrollbar-thumb-[#3A4852]">
          <table className="w-full min-w-[720px]">
            <thead className="sticky top-0 z-10 bg-[#1C2227]">
              {table.getHeaderGroups().map((hg) => (
                <tr
                  key={hg.id}
                  className="flex min-h-[52px] items-center border-b border-[#2C363E] px-6"
                >
                  {hg.headers.map((header) => (
                    <th
                      key={header.id}
                      className={`${addressColClass(header.column.id)} text-left`}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className="flex min-h-[56px] items-center border-b border-[#2C363E]/60 px-6 transition-colors last:border-b-0 hover:bg-[#1C2227]/40"
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={addressColClass(cell.column.id)}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
